/**
 * The three tabs of `/raporty`, their order, and how the chosen one rides in the address.
 *
 * KANON `Raporty` (17 September 2026) puts them at the level of the PAGE rather than inside a card:
 * **Ankiety** is the list of forms, **Kto ma którą** the client × survey matrix, **Do przejrzenia**
 * the queue of submissions waiting for the trainer's eye. They are three readings of one subject and
 * each owns its own reads, so only the visible one is mounted.
 *
 * ── The keys are Polish because the app's addresses are ──────────────────────────────────────
 * `/klienci`, `/klienci/archiwum`, `/plans/nowy`, and now `/raporty?tab=kto`. A URL is something a
 * person reads, and this app reads Polish to its trainers. They are NOT mobile's — mobile has no
 * such screen, so there is no deep link to keep compatible, which is the one thing that forced the
 * client detail's English keys.
 *
 * ── An unrecognised `?tab=` is DROPPED, not defaulted into the URL ───────────────────────────
 * The rule and its reason are `_shell.klienci.$clientId.tsx`'s: writing a default back would rewrite
 * a link the trainer pasted, and the screen falls back on its own anyway. `/raporty` and
 * `/raporty?tab=ankiety` would otherwise be two addresses for one screen — the second is what a
 * trainer would paste into a message and the first is what every link in the app produces.
 *
 * No React, no DOM, no query hook, no i18next: `t` arrives as a parameter.
 */

import { tabFocusTarget } from '~/shared/ui/TabStrip';

export const REPORT_TABS = ['ankiety', 'kto', 'przegladu'] as const;

export type ReportTab = (typeof REPORT_TABS)[number];

export const DEFAULT_REPORT_TAB: ReportTab = 'ankiety';

export function isReportTab(value: unknown): value is ReportTab {
  return typeof value === 'string' && (REPORT_TABS as readonly string[]).includes(value);
}

/**
 * The screen's search parameters.
 *
 * One key today, shaped as the plan library's is — an optional member per parameter, absent meaning
 * "not in the URL" — so a second parameter is one entry here and one parser below.
 */
export interface ReportsSearch {
  readonly tab?: ReportTab;
}

/**
 * The route's `validateSearch`.
 *
 * It lives in the slice rather than in `app/routes/`, because which parameters this screen
 * understands is the screen's decision: a route file is a wiring declaration, and anything a second
 * route could want does not live there.
 */
export function validateReportsSearch(raw: Record<string, unknown>): ReportsSearch {
  const tab = isReportTab(raw.tab) ? raw.tab : undefined;
  // OMITTED rather than set to `undefined`: TanStack Router serialises the object back into the URL
  // and an explicit `undefined` leaves a bare `?tab=` behind. The DEFAULT is omitted too — see the
  // header.
  return tab === undefined || tab === DEFAULT_REPORT_TAB ? {} : { tab };
}

/** Which tab an address is asking for. Anything this screen does not recognise is Ankiety. */
export function reportTabOf(search: ReportsSearch): ReportTab {
  return isReportTab(search.tab) ? search.tab : DEFAULT_REPORT_TAB;
}

/** The address a tab should be at. The default serialises to nothing — see the header. */
export function reportTabSearch(tab: ReportTab): ReportsSearch {
  return tab === DEFAULT_REPORT_TAB ? {} : { tab };
}

/** The word on a tab. Web copy: mobile has no such screen, so there is nothing upstream to reuse. */
export function reportTabLabel(tab: ReportTab, t: (key: string) => string): string {
  return t(`web:reports.tabs.${tab}`);
}

/**
 * Where a key press moves focus inside this screen's strip.
 *
 * The RULE is `shared/ui/TabStrip`'s. What lives here is the ORDER it walks, because `REPORT_TABS`
 * is this slice's declaration of which tabs exist.
 */
export function reportTabFocusTarget(key: string, current: ReportTab): ReportTab | null {
  return tabFocusTarget(key, current, REPORT_TABS);
}
